import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { H3 } from '../containers/Headers';
import Checkbox from '../formInputs/Checkbox';
import Icon from '../icon/Icon';
import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { sortList } from '../../util/sortList';

const sortByType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired
});

class SortOptions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      completeList: null,
      isOpen: false,
      sortingBy: []
    };
  }

  componentDidMount() {
    const { defaultValue } = this.props;
    document.addEventListener('mousedown', this.handleClickOutside);
    if (defaultValue) {
      setTimeout(() => {
        this.handleSortByClick(defaultValue, true);
      }, 500);
    }
  }

  componentDidUpdate(prevProps) {
    const { list, defaultValue, handleExternalSort } = this.props;
    const { completeList } = this.state;
    if (list && list.length) {
      if (
        (prevProps.list && prevProps.list.length !== list.length) || // List swapped out completely
        (defaultValue &&
          prevProps.list &&
          prevProps.list[0] &&
          prevProps.list[0][defaultValue.value] !== list[0][defaultValue.value]) || // List updated
        (!completeList && list.length > 0) || // State isn't set yet
        (completeList && list.length !== completeList.length) // State set, but list changed
      ) {
        this.setCompleteList(list);

        if (defaultValue && !handleExternalSort) {
          this.handleSortByClick(defaultValue, true);
        }
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    this.setSortingBy(null);
  }

  setSortOptionsRef = node => (this.sortOptionsRef = node);

  setCompleteList = list => this.setState({ completeList: list });

  setIsOpen = bool => this.setState({ isOpen: bool });

  setSortingBy = sortingBy => this.setState({ sortingBy });

  toggleOpen = () => {
    const { isOpen } = this.state;
    return this.setIsOpen(!isOpen);
  };

  handleClickOutside = event => {
    const { isOpen } = this.state;
    if (this.sortOptionsRef && !this.sortOptionsRef.contains(event.target) && isOpen) {
      this.toggleOpen();
    }
  };

  handleSortByClick = async ({ value, name, desc, isNumber }, ignoreDirection) => {
    const { sortingBy } = this.state;
    const { handleExternalSort, list, handleSortOptionsChange } = this.props;
    const isRepeatClick = sortingBy && sortingBy.name === name;
    const isExternalSort = handleExternalSort && desc === undefined;
    const newDirection = !(sortingBy && sortingBy.desc);
    const newState = {
      value,
      name,
      desc: isRepeatClick ? newDirection : false,
      isNumber
    };

    // eslint-disable-next-line
    newState.desc = ignoreDirection ? (desc ? desc : false) : newState.desc;
    if (newState.value) {
      this.setState({ sortingBy: newState }, () =>
        // eslint-disable-next-line no-nested-ternary
        isExternalSort ? handleExternalSort(newState) : handleSortOptionsChange ? this.handleSortAndFilter(list) : false
      );
    }
    return this.setState({ sortingBy: newState });
  };

  handleFilterChange = (checkVal, filterBy) => {
    const { list } = this.props;
    const { completeList } = this.state;
    return this.handleSortAndFilter(filterBy && checkVal.target.checked ? list.filter(filterBy) : completeList);
  };

  handleSortAndFilter = list => {
    const { sortingBy } = this.state;
    const { handleSortOptionsChange } = this.props;
    const updatedList =
      sortingBy && sortingBy.value ? sortList(list, sortingBy.value, sortingBy.isNumber, !sortingBy.desc) : list;
    handleSortOptionsChange(updatedList, sortingBy);
  };

  resetOptions = () => {
    const { defaultValue } = this.props;
    this.handleSortByClick(defaultValue, true);
  };

  render() {
    const { sortBy, toggleView, filterBy, currentView } = this.props;
    const { sortingBy, isOpen } = this.state;
    return (
      <div className='sortOptions d-sm-flx flx-jc-sm-fe mr3-sm' ref={this.setSortOptionsRef}>
        <button
          className='controls flex-container row flx-ai-sm-c p0-sm'
          onClick={this.toggleOpen}
          type='button'
          data-desc='sortOptions__button_toggle-view'
        >
          <H3 className='d-sm-h d-lg-ib pr3-sm'>
            <TranslatedCopy id='global.sortBy' />
          </H3>
          <Icon name='filter' alt='change view' />
        </button>
        {isOpen && (
          <div data-testid='sortOptions-content' className='sortOptions__content border-light-grey'>
            {sortBy && (
              <div>
                <div className='sortOptions__content_section border-bottom body-3 u-bold'>
                  <TranslatedCopy id='global.sortBy' />
                </div>
                <div className='sortOptions__content_section'>
                  <ul>
                    {sortBy.map(sort => {
                      const isSelected =
                        sortingBy &&
                        sortingBy.name &&
                        sortingBy.value &&
                        sort &&
                        sort.name &&
                        (sortingBy.name === sort.name ||
                          (sortingBy.value &&
                            sortingBy.value.toString().toLowerCase() ===
                              sort.name.replace(/\s/g, '').toString().toLowerCase()));
                      const sortByClick = () => this.handleSortByClick(sort);
                      return (
                        <li key={`sortbyButton-${sort.value}`}>
                          <button
                            type='button'
                            className={classnames(
                              'flex-container row space-between pt1-sm pb2-sm prl0-sm u-full-width ta-sm-l body-3',
                              {
                                'u-bold': isSelected
                              }
                            )}
                            onClick={sortByClick}
                            data-desc={`sortOptions__button_sort-by-${sort.value}`}
                          >
                            {new TranslatedCopy({ id: sort.name }).toString()}
                            {isSelected &&
                              (sortingBy.desc ? (
                                <Icon name='arrow-down' alt='descending' />
                              ) : (
                                <Icon name='arrow-up' alt='ascending' />
                              ))}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
            {toggleView && (
              <div
                className={classnames(
                  'sortOptions__content_gridlist sortOptions__content_section',
                  'flex-container row space-between align-center border-top'
                )}
              >
                <div className='body-3 text-uppercase u-bold'>
                  <TranslatedCopy id='global.viewOptions' />
                </div>
                <div className='flex-container row align-center viewOptions__button'>
                  <button
                    type='button'
                    className={classnames('va-c-sm', { isSelected: currentView === 'grid' })}
                    onClick={() => toggleView('grid')}
                    title='change to grid view'
                    data-desc='viewOptions__button_grid-view'
                  >
                    <Icon name='view-grid' alt='grid icon' />
                  </button>
                  <button
                    type='button'
                    className={classnames('va-c-sm', { isSelected: currentView === 'list' })}
                    onClick={() => toggleView('list')}
                    title='change to list view'
                    data-desc='viewOptions__button_list-view'
                  >
                    <Icon name='view-list' alt='list icon' />
                  </button>
                </div>
              </div>
            )}
            {filterBy && (
              <div
                className={classnames(
                  'sortOptions__content_section',
                  'flex-container row space-between',
                  'align-center border-top'
                )}
              >
                <ul>
                  {filterBy.map(filter => (
                    <li>
                      <Checkbox label={filter.name} onChange={val => this.handleFilterChange(val, filter.value)} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

SortOptions.propTypes = {
  list: PropTypes.any,
  currentView: PropTypes.string,
  sortBy: PropTypes.arrayOf(sortByType),
  defaultValue: sortByType,
  filterBy: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.func.isRequired
    })
  ),
  toggleView: PropTypes.func,
  handleSortOptionsChange: PropTypes.func,
  handleExternalSort: PropTypes.func
};

SortOptions.defaultProps = {
  list: null,
  currentView: null,
  sortBy: null,
  filterBy: null,
  toggleView: null,
  handleSortOptionsChange: null,
  handleExternalSort: null,
  defaultValue: null
};

export default SortOptions;
